import apiClient from "../../api/api";

/**
 * Retrieves a paginated list of documents associated with a project.
 *
 * @async
 * @function loadDocuments
 * @param {number|string} projectId - ID of the project whose documents will be retrieved.
 * @param {number} [page=1] - Page number to retrieve.
 * @param {number} [limit=100] - Maximum number of documents to retrieve per page.
 * @returns {Promise<any>} Resolves with the paginated documents response from the API.
 * @throws {Error} Throws if the request fails.
 */
export const loadDocuments = async (projectId, page = 1, limit = 100) => {
    const params = {
        page,
        limit
    };

    const response = await apiClient.get(`projects/${projectId}/document`, { params });

    return response.data;
};

/**
 * Retrieves a specific document associated with a project as a Blob.
 *
 * @async
 * @function loadDocumentById
 * @param {number|string} projectId - ID of the project that owns the document.
 * @param {number|string} documentId - ID of the document to retrieve.
 * @returns {Promise<Blob>} Resolves with the document's binary content.
 * @throws {Error} Throws if the request fails.
 */
export const loadDocumentById = async (projectId, documentId) => {
  const response = await apiClient.get(
    `/projects/${projectId}/document/${documentId}`,
    {
      responseType: "blob",
    }
  );

  return response.data;
};

/**
 * Adds a new document to a project.
 *
 * @async
 * @function addDocument
 * @param {number|string} projectId - ID of the project to which the document will be added.
 * @param {any} document - Document payload to be sent to the API.
 * @returns {Promise<any>} Resolves with the response returned after the document is added.
 * @throws {Error} Throws if the request fails.
 */
export const addDocument = async (projectId, document) => {
    const response = await apiClient.post(`/projects/${projectId}/document`, document);

    return response.data;
};

/**
 * Deletes a specific document associated with a project.
 *
 * @async
 * @function deleteDocument
 * @param {number|string} projectId - ID of the project that owns the document.
 * @param {number|string} documentId - ID of the document to delete.
 * @returns {Promise<any>} Resolves with the response returned after the document is deleted.
 * @throws {Error} Throws if the request fails.
 */
export const deleteDocument = async (projectId, documentId) => {
    const response = await apiClient.delete(`projects/${projectId}/document/${documentId}`);

    return response.data;
};